import { CloudUpload, WifiOff } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import type { Clock } from '@obraplan/core/application/ports/clock';
import { Badge } from '@/components/ui/badge';
import { useContainer } from '@/features/session/use-session';
import { useQueueCounts } from './use-outbox-sync';

const FUSO = 'America/Sao_Paulo';

const HORA = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: FUSO,
});
const DIA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: FUSO });

/** `08:12` quando é de hoje; `27/10 18:40` quando é de outro dia — o de ontem não passa por hoje. */
function quandoFoiGuardado(iso: string, clock: Clock): string {
  const instante = new Date(iso);
  const hora = HORA.format(instante);
  return DIA.format(instante) === DIA.format(clock.now())
    ? hora
    : `${DIA.format(instante)} ${hora}`;
}

/** "3 aguardando envio" — e, no celular, só o ícone e o número. */
function naFila(pendentes: number): string {
  return pendentes === 1 ? '1 aguardando envio' : `${String(pendentes)} aguardando envio`;
}

/**
 * "Sem conexão · dados de 08:12", na barra de cima (Fase 43, RN-599).
 *
 * Sem sinal, a hora é a do dado guardado mais antigo que a tela recebeu desde que a conexão caiu:
 * quem confere no pavimento sabe de quando é o que está vendo. Com sinal de volta, some — e as
 * leituras abertas são refeitas pela rede.
 *
 * O que foi feito sem sinal e ainda não subiu (etapa 2) é contado junto; com sinal, enquanto a
 * fila sobe, o indicador mostra só a contagem (RN-605).
 */
export function ConnectionIndicator() {
  const { clock, connection } = useContainer();
  const agora = useSyncExternalStore(connection.subscribe, connection.snapshot);
  // O recusado não está "aguardando envio": ele espera a pessoa, no lugar em que foi feito.
  const pendentes = useQueueCounts().pending;

  if (agora.online) {
    if (pendentes === 0) return null;
    return (
      <Badge
        tone="info"
        role="status"
        className="shrink-0"
        title="O que foi feito sem sinal está subindo para o servidor."
        icon={<CloudUpload aria-hidden="true" />}
      >
        <span className="sr-only sm:not-sr-only">{naFila(pendentes)}</span>
        <span aria-hidden="true" className="sm:hidden">
          {pendentes}
        </span>
      </Badge>
    );
  }

  // No celular, com a hora, fica o ícone e a hora: com o texto inteiro, o perfil descia para uma
  // faixa só dele, e o cabeçalho fixo comia mais uma linha da Conferência. As palavras continuam
  // para o leitor de tela (`sr-only`) e no `title`.
  return (
    <Badge
      tone="warn"
      role="status"
      className="shrink-0"
      title="O servidor não está respondendo. O que aparece é o que este aparelho guardou."
      icon={<WifiOff aria-hidden="true" />}
    >
      {agora.oldestServedAt === undefined ? (
        'Sem conexão'
      ) : (
        <span>
          <span className="sr-only sm:not-sr-only">Sem conexão · dados de </span>
          {quandoFoiGuardado(agora.oldestServedAt, clock)}
        </span>
      )}
      {pendentes > 0 && <span className="sr-only sm:not-sr-only"> · {naFila(pendentes)}</span>}
    </Badge>
  );
}
